import React, { useState, useEffect } from 'react';
import { votesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { buildSides } from '../utils/battleSides';

// Re-exported for consumers that already import it from here.
export { buildSides };

const sideLabel = (side) =>
  side.artists.map((a) => a.display_name || a.username).join(' & ');

const BattleVoting = ({ videoId, artists }) => {
  const { user } = useAuth();
  const [voteData, setVoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  const sides = buildSides(artists);
  const twoSided =
    sides.length === 2 && sides[0].artists.length > 0 && sides[1].artists.length > 0;

  useEffect(() => {
    if (twoSided) {
      fetchVotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, artists]);

  const fetchVotes = async () => {
    try {
      const response = await votesAPI.getVotes(videoId);
      setVoteData(response.data);
    } catch (error) {
      console.error('Error fetching votes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (side) => {
    if (!user || voting) return;
    setVoting(true);
    try {
      await votesAPI.castVote(videoId, side);
      await fetchVotes();
    } catch (error) {
      console.error('Error casting vote:', error);
    } finally {
      setVoting(false);
    }
  };

  // Don't render for anything that isn't a two-sided battle.
  if (!twoSided) return null;

  const hasVoted = voteData?.has_voted;
  const userVoteSide = voteData?.user_vote_side;
  const totalVotes = voteData?.total_votes || 0;
  const resultSides = voteData?.sides || [];

  const resultForSide = (side) =>
    resultSides.find((s) => s.side === side) || { votes: 0, percentage: 0 };

  // Badge from the two side percentages.
  const getBadge = () => {
    if (!hasVoted || resultSides.length < 2) return null;
    const pcts = resultSides.map((r) => r.percentage);
    const maxPct = Math.max(...pcts);
    const minPct = Math.min(...pcts);
    if (maxPct >= 80) return { label: 'Dominant', color: 'battle-red' };
    if (maxPct <= 55 && minPct >= 45) return { label: 'Controversial', color: 'battle-gold' };
    return null;
  };

  const badge = getBadge();

  return (
    <div className="card p-6 mt-6" data-testid="battle-voting">
      <h3 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight text-white text-center mb-6">
        WHO WON THIS <span className="text-brand-yellow">BATTLE</span>?
      </h3>

      {loading ? (
        <div className="flex justify-center py-8">
          {/* battle-red kept: live activity spinner = semantic danger/live state */}
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-battle-red" />
        </div>
      ) : !user ? (
        /* Unauthenticated state */
        <div className="text-center py-6">
          <div className="flex items-center justify-center gap-6 mb-6">
            <SideCard side={sides[0]} disabled />
            <VsBadge />
            <SideCard side={sides[1]} disabled />
          </div>
          <p className="text-gray-400 text-sm">
            <span className="text-brand-yellow font-bold">Sign in to vote</span> and see what the community thinks
          </p>
        </div>
      ) : hasVoted ? (
        /* Results state */
        <div>
          {badge && (
            <div className="flex justify-center mb-4">
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
                  badge.color === 'battle-red'
                    ? 'bg-battle-red/20 text-battle-red border-battle-red/30'
                    : 'bg-brand-yellow/15 text-brand-yellow border-brand-yellow/30'
                }`}
              >
                {badge.label}
              </span>
            </div>
          )}

          <div className="space-y-4">
            {sides.map((side) => {
              const result = resultForSide(side.side);
              const isWinner = result.percentage > 50;
              const isPick = userVoteSide === side.side;

              return (
                <div key={side.side} className="relative" data-testid={`side-result-${side.side}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex -space-x-2 shrink-0">
                        {side.artists.map((artist) => (
                          <Avatar key={artist.id} artist={artist} />
                        ))}
                      </div>
                      <span className="text-white font-bold text-sm uppercase tracking-tight font-display truncate">
                        {sideLabel(side)}
                      </span>
                      {isPick && (
                        <span
                          data-testid={`your-pick-${side.side}`}
                          className="text-xs text-brand-yellow font-bold flex items-center gap-1 shrink-0"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Your pick
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-lg font-black ${isWinner ? 'text-brand-yellow' : 'text-gray-400'}`}
                    >
                      {result.percentage}%
                    </span>
                  </div>

                  {/* Percentage bar */}
                  <div className="h-2.5 bg-battle-dark rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ease-out ${
                        isWinner ? 'bg-gradient-to-r from-army to-brand-yellow' : 'bg-gray-700'
                      }`}
                      style={{ width: `${result.percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-center text-gray-500 text-xs mt-4 uppercase tracking-wider">
            {totalVotes} votes total
          </p>
        </div>
      ) : (
        /* Voting state */
        <div className="flex items-center justify-center gap-4 md:gap-6">
          <button
            data-testid={`vote-btn-side-${sides[0].side}`}
            onClick={() => handleVote(sides[0].side)}
            disabled={voting}
            className="flex-1 max-w-[200px]"
          >
            <SideCard side={sides[0]} interactive voting={voting} />
          </button>

          <VsBadge large />

          <button
            data-testid={`vote-btn-side-${sides[1].side}`}
            onClick={() => handleVote(sides[1].side)}
            disabled={voting}
            className="flex-1 max-w-[200px]"
          >
            <SideCard side={sides[1]} interactive voting={voting} />
          </button>
        </div>
      )}
    </div>
  );
};

const VsBadge = ({ large }) => (
  <span
    className={`text-brand-yellow font-black shrink-0 px-2 py-1 rounded ${
      large ? 'text-2xl md:text-3xl' : 'text-2xl'
    }`}
    style={{
      border: '1px solid rgba(245,230,66,0.30)',
      background: 'rgba(245,230,66,0.08)',
    }}
  >
    VS
  </span>
);

const Avatar = ({ artist }) =>
  artist.avatar_url ? (
    <img
      src={artist.avatar_url}
      alt={artist.display_name || artist.username}
      className="w-8 h-8 rounded-full border border-gray-700"
    />
  ) : (
    <div className="w-8 h-8 rounded-full bg-battle-gray-light flex items-center justify-center text-white text-sm font-bold border border-gray-700">
      {(artist.display_name || artist.username).charAt(0).toUpperCase()}
    </div>
  );

/**
 * A card for one side of the battle — renders one artist (1v1) or several (2v2).
 */
const SideCard = ({ side, interactive, disabled, voting }) => {
  return (
    <div
      className={`flex flex-col items-center p-4 rounded-xl border transition-all duration-300 ${
        interactive && !voting
          ? 'border-army/50 bg-battle-gray hover:border-brand-yellow/50 hover:shadow-[0_0_20px_rgba(245,230,66,0.12)] hover:scale-105 cursor-pointer'
          : 'border-gray-800 bg-battle-gray'
      } ${disabled ? 'opacity-70' : ''}`}
    >
      <div className="flex -space-x-3 mb-3">
        {side.artists.map((artist) =>
          artist.avatar_url ? (
            <img
              key={artist.id}
              src={artist.avatar_url}
              alt={artist.display_name || artist.username}
              className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-gray-700"
            />
          ) : (
            <div
              key={artist.id}
              className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-army/20 border-2 border-army/40 flex items-center justify-center text-brand-yellow text-2xl md:text-3xl font-black"
            >
              {(artist.display_name || artist.username).charAt(0).toUpperCase()}
            </div>
          )
        )}
      </div>
      <span className="text-white font-bold text-sm md:text-base uppercase tracking-tight font-display text-center">
        {sideLabel(side)}
      </span>
      {interactive && !voting && (
        <span className="mt-2 text-xs text-gray-500 uppercase tracking-widest font-bold">Vote</span>
      )}
      {voting && (
        /* battle-red kept: live activity spinner = semantic */
        <div className="mt-2 animate-spin rounded-full h-4 w-4 border-b-2 border-battle-red" />
      )}
    </div>
  );
};

export default BattleVoting;
